// Hand-maintained — override generation
// Hand-maintained — safe to edit directly. See lib/gpt_core/sdk/CLAUDE.md for context.
// mix update.sdks preserves this file (only overwrites empty stubs). Do NOT add `return {};`.

import {
  // --- Tickets ---
  getAdminSupportTicketsById,
  getAdminSupportTicketsWorkspaceByWorkspaceId,
  postAdminSupportTickets,
  patchAdminSupportTicketsById,
  patchAdminSupportTicketsByIdAssign,
  patchAdminSupportTicketsByIdResolve,
  patchAdminSupportTicketsByIdClose,
  patchAdminSupportTicketsByIdReopen,
  patchAdminSupportTicketsByIdMerge,
  deleteAdminSupportTicketsById,
  // --- Ticket Messages ---
  getAdminSupportTicketMessagesTicketByTicketId,
  postAdminSupportTicketMessages,
  // --- Ticket Attachments ---
  getAdminSupportTicketAttachmentsMessageByTicketMessageId,
  postAdminSupportTicketAttachments,
  // --- Ticket Ratings ---
  postAdminSupportTicketRatings,
  // --- Tags ---
  getAdminSupportTagsWorkspaceByWorkspaceId,
  postAdminSupportTags,
  deleteAdminSupportTagsById,
  // --- Queues ---
  getAdminSupportQueuesById,
  getAdminSupportQueuesApplicationByApplicationId,
  postAdminSupportQueues,
  patchAdminSupportQueuesById,
  deleteAdminSupportQueuesById,
  // --- Queue Members ---
  getAdminSupportQueueMembersQueueByQueueId,
  getAdminSupportQueueMembersApplicationByApplicationId,
  postAdminSupportQueueMembers,
  patchAdminSupportQueueMembersById,
  deleteAdminSupportQueueMembersById,
  // --- Routing Rules ---
  getAdminSupportRoutingRulesById,
  getAdminSupportRoutingRulesApplicationByApplicationId,
  postAdminSupportRoutingRules,
  patchAdminSupportRoutingRulesById,
  deleteAdminSupportRoutingRulesById,
  // --- SLA Policies ---
  getAdminSupportSlaPoliciesById,
  getAdminSupportSlaPoliciesApplicationByApplicationId,
  postAdminSupportSlaPolicies,
  patchAdminSupportSlaPoliciesById,
  deleteAdminSupportSlaPoliciesById,
  // --- AI Config ---
  postAdminSupportAiConfigs,
  getAdminSupportAiConfigsWorkspaceByWorkspaceId,
  // --- Channel Capture Config ---
  postAdminSupportChannelCaptureConfigs,
  getAdminSupportChannelCaptureConfigsWorkspaceByWorkspaceId,
  // --- Sync Config ---
  postAdminSupportSyncConfigs,
  getAdminSupportSyncConfigsWorkspaceByWorkspaceId,
  patchAdminSupportSyncConfigsById,
  deleteAdminSupportSyncConfigsById,
  // --- Canned Responses ---
  getAdminSupportCannedResponsesWorkspaceByWorkspaceId,
  postAdminSupportCannedResponses,
  patchAdminSupportCannedResponsesById,
  deleteAdminSupportCannedResponsesById,
} from "../_internal/sdk.gen";
import type { RequestOptions } from "../base-client";
import { RequestBuilder } from "../request-builder";

/**
 * Support domain namespace — tickets, queues, SLA policies, routing, and AI configuration.
 *
 * Covers the full support lifecycle: manage tickets through assign/resolve/close/reopen/merge,
 * configure queues with AI modes, set SLA policies, define routing rules, and manage
 * canned responses and channel capture configs.
 */
export function createSupportNamespace(rb: RequestBuilder) {
  return {
    // --- Tickets ---
    tickets: {
      /** Get a support ticket by ID */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          getAdminSupportTicketsById,
          { path: { id } },
          options,
        );
      },
      /** List tickets for a workspace */
      listByWorkspace: async (
        workspaceId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSupportTicketsWorkspaceByWorkspaceId,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },
      /** Create a new support ticket */
      create: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSupportTickets,
          { body: { data: { type: "support-ticket", attributes } } },
          options,
        );
      },
      /** Update a support ticket */
      update: async (
        id: string,
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSupportTicketsById,
          {
            path: { id },
            body: { data: { type: "support-ticket", id, attributes } },
          },
          options,
        );
      },
      /** Assign a ticket to an agent or queue */
      assign: async (
        id: string,
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSupportTicketsByIdAssign,
          {
            path: { id },
            body: { data: { type: "support-ticket", id, attributes } },
          },
          options,
        );
      },
      /** Resolve a ticket */
      resolve: async (
        id: string,
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSupportTicketsByIdResolve,
          {
            path: { id },
            body: { data: { type: "support-ticket", id, attributes } },
          },
          options,
        );
      },
      /** Close a resolved ticket */
      close: async (
        id: string,
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSupportTicketsByIdClose,
          {
            path: { id },
            body: { data: { type: "support-ticket", id, attributes } },
          },
          options,
        );
      },
      /** Reopen a closed or resolved ticket */
      reopen: async (
        id: string,
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSupportTicketsByIdReopen,
          {
            path: { id },
            body: { data: { type: "support-ticket", id, attributes } },
          },
          options,
        );
      },
      /** Merge a ticket into another ticket */
      merge: async (
        id: string,
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSupportTicketsByIdMerge,
          {
            path: { id },
            body: { data: { type: "support-ticket", id, attributes } },
          },
          options,
        );
      },
      /** Delete a support ticket */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminSupportTicketsById,
          { path: { id } },
          options,
        );
      },
    },

    // --- Messages ---
    messages: {
      /** List messages for a ticket */
      listByTicket: async (ticketId: string, options?: RequestOptions) => {
        return rb.execute(
          getAdminSupportTicketMessagesTicketByTicketId,
          { path: { ticket_id: ticketId } },
          options,
        );
      },
      /** Create a message on a ticket */
      create: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSupportTicketMessages,
          { body: { data: { type: "support-ticket-message", attributes } } },
          options,
        );
      },
    },

    // --- Attachments ---
    attachments: {
      /** List attachments for a ticket message */
      listByMessage: async (messageId: string, options?: RequestOptions) => {
        return rb.execute(
          getAdminSupportTicketAttachmentsMessageByTicketMessageId,
          { path: { ticket_message_id: messageId } },
          options,
        );
      },
      /** Create an attachment on a ticket message */
      create: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSupportTicketAttachments,
          { body: { data: { type: "support-ticket-attachment", attributes } } },
          options,
        );
      },
    },

    // --- Ratings ---
    ratings: {
      /** Create or update a satisfaction rating for a ticket */
      create: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSupportTicketRatings,
          { body: { data: { type: "support-ticket-rating", attributes } } },
          options,
        );
      },
    },

    // --- Tags ---
    tags: {
      /** List tags for a workspace */
      listByWorkspace: async (
        workspaceId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSupportTagsWorkspaceByWorkspaceId,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },
      /** Create a tag */
      create: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSupportTags,
          { body: { data: { type: "support-tag", attributes } } },
          options,
        );
      },
      /** Delete a tag */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminSupportTagsById,
          { path: { id } },
          options,
        );
      },
    },

    // --- Queues ---
    queues: {
      /** Get a queue by ID */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute(getAdminSupportQueuesById, { path: { id } }, options);
      },
      /** List queues for an application */
      listByApplication: async (
        applicationId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSupportQueuesApplicationByApplicationId,
          { path: { application_id: applicationId } },
          options,
        );
      },
      /** Create a queue */
      create: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSupportQueues,
          { body: { data: { type: "support-queue", attributes } } },
          options,
        );
      },
      /** Update a queue */
      update: async (
        id: string,
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSupportQueuesById,
          {
            path: { id },
            body: { data: { type: "support-queue", id, attributes } },
          },
          options,
        );
      },
      /** Delete a queue */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminSupportQueuesById,
          { path: { id } },
          options,
        );
      },
    },

    // --- Queue Members ---
    queueMembers: {
      /** List members of a queue */
      listByQueue: async (queueId: string, options?: RequestOptions) => {
        return rb.execute(
          getAdminSupportQueueMembersQueueByQueueId,
          { path: { queue_id: queueId } },
          options,
        );
      },
      /** List all queue members for an application */
      listByApplication: async (
        applicationId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSupportQueueMembersApplicationByApplicationId,
          { path: { application_id: applicationId } },
          options,
        );
      },
      /** Add a member to a queue */
      create: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSupportQueueMembers,
          { body: { data: { type: "support-queue-member", attributes } } },
          options,
        );
      },
      /** Update a queue member */
      update: async (
        id: string,
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSupportQueueMembersById,
          {
            path: { id },
            body: { data: { type: "support-queue-member", id, attributes } },
          },
          options,
        );
      },
      /** Remove a member from a queue */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminSupportQueueMembersById,
          { path: { id } },
          options,
        );
      },
    },

    // --- Routing Rules ---
    routingRules: {
      /** Get a routing rule by ID */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          getAdminSupportRoutingRulesById,
          { path: { id } },
          options,
        );
      },
      /** List routing rules for an application */
      listByApplication: async (
        applicationId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSupportRoutingRulesApplicationByApplicationId,
          { path: { application_id: applicationId } },
          options,
        );
      },
      /** Create a routing rule */
      create: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSupportRoutingRules,
          { body: { data: { type: "support-routing-rule", attributes } } },
          options,
        );
      },
      /** Update a routing rule */
      update: async (
        id: string,
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSupportRoutingRulesById,
          {
            path: { id },
            body: { data: { type: "support-routing-rule", id, attributes } },
          },
          options,
        );
      },
      /** Delete a routing rule */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminSupportRoutingRulesById,
          { path: { id } },
          options,
        );
      },
    },

    // --- SLA Policies ---
    slaPolicies: {
      /** Get an SLA policy by ID */
      get: async (id: string, options?: RequestOptions) => {
        return rb.execute(
          getAdminSupportSlaPoliciesById,
          { path: { id } },
          options,
        );
      },
      /** List SLA policies for an application */
      listByApplication: async (
        applicationId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSupportSlaPoliciesApplicationByApplicationId,
          { path: { application_id: applicationId } },
          options,
        );
      },
      /** Create an SLA policy */
      create: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSupportSlaPolicies,
          { body: { data: { type: "support-sla-policy", attributes } } },
          options,
        );
      },
      /** Update an SLA policy */
      update: async (
        id: string,
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSupportSlaPoliciesById,
          {
            path: { id },
            body: { data: { type: "support-sla-policy", id, attributes } },
          },
          options,
        );
      },
      /** Delete an SLA policy */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminSupportSlaPoliciesById,
          { path: { id } },
          options,
        );
      },
    },

    // --- AI Config ---
    aiConfig: {
      /** Get AI config for a workspace */
      getForWorkspace: async (
        workspaceId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSupportAiConfigsWorkspaceByWorkspaceId,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },
      /** Create or update AI config */
      upsert: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSupportAiConfigs,
          { body: { data: { type: "support-ai-config", attributes } } },
          options,
        );
      },
    },

    // --- Channel Capture Config ---
    channelCaptureConfig: {
      /** Get channel capture config for a workspace */
      getForWorkspace: async (
        workspaceId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSupportChannelCaptureConfigsWorkspaceByWorkspaceId,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },
      /** Create or update channel capture config */
      upsert: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSupportChannelCaptureConfigs,
          {
            body: {
              data: { type: "support-channel-capture-config", attributes },
            },
          },
          options,
        );
      },
    },

    // --- Sync Config ---
    syncConfig: {
      /** List sync configs for a workspace */
      listByWorkspace: async (
        workspaceId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSupportSyncConfigsWorkspaceByWorkspaceId,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },
      /** Create a sync config */
      create: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSupportSyncConfigs,
          { body: { data: { type: "support-sync-config", attributes } } },
          options,
        );
      },
      /** Update a sync config */
      update: async (
        id: string,
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSupportSyncConfigsById,
          {
            path: { id },
            body: { data: { type: "support-sync-config", id, attributes } },
          },
          options,
        );
      },
      /** Delete a sync config */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminSupportSyncConfigsById,
          { path: { id } },
          options,
        );
      },
    },

    // --- Canned Responses ---
    cannedResponses: {
      /** List canned responses for a workspace */
      listByWorkspace: async (
        workspaceId: string,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          getAdminSupportCannedResponsesWorkspaceByWorkspaceId,
          { path: { workspace_id: workspaceId } },
          options,
        );
      },
      /** Create a canned response */
      create: async (
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          postAdminSupportCannedResponses,
          { body: { data: { type: "support-canned-response", attributes } } },
          options,
        );
      },
      /** Update a canned response */
      update: async (
        id: string,
        attributes: Record<string, unknown>,
        options?: RequestOptions,
      ) => {
        return rb.execute(
          patchAdminSupportCannedResponsesById,
          {
            path: { id },
            body: { data: { type: "support-canned-response", id, attributes } },
          },
          options,
        );
      },
      /** Delete a canned response */
      delete: async (id: string, options?: RequestOptions): Promise<true> => {
        return rb.executeDelete(
          deleteAdminSupportCannedResponsesById,
          { path: { id } },
          options,
        );
      },
    },
  };
}
